import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Skeleton, Tooltip } from "@mui/material";

import {
  flexRender,
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from "@tanstack/react-table";

import { useVirtualizer } from "@tanstack/react-virtual";

import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";

import { useAuth } from "../../../context/AuthProvider";
import { useShowAlert } from "../../../context/AlertContext";
import { usePermission } from "../../../context/PermissionContext";
import { useContextApi } from "../../../context/ContextApi";

import Messages from "../../../constants/Messages";

import EditStock from "../stockbatch/EditStock";
import ViewStockDetails from "../stockbatch/ViewStockDetails";
import BarcodeGenerator from "../product/BarCode";

import uRLs from "../../../utils/baseUtils";
import { secondaryQuanity } from "../../../utils/ProductHandleChange";

const ViewStockReport = ({
  isData,
  isPreLoading,
  isLoading,
  setStockEditModalOpen,
  closeEditModal,
  handleUpdate,
  isStockEditModalOpen,
  setStockOutModalOpen,
  isStockOutModalOpen,
  closeStockOutModal,
  handleStockTrash,
}) => {
  const { isUser } = useAuth();
  const showAlert = useShowAlert();
  const { permission, hasPermission } = usePermission();
  const [isEditStockData, setISEditStockData] = useState();
  const [productViewOpen, setProductViewOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [selectedBarcode, setSelectedBarcode] = useState("");
  const { getColor } = useContextApi();
  const tableContainerRef = useRef(null);

  const columnHelper = createColumnHelper();

  const [isStockEditData, setStockEditData] = useState([]);

  useEffect(() => {
    setStockEditData(isData || []);
  }, [isData]);

  const tableData = useMemo(() => isStockEditData || [], [isStockEditData]);

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handlecloseView = () => {
    setProductViewOpen(false);
  };

  const openEditModal = useCallback(
    (cItem, cIndex) => {
      if (hasPermission("STOCK", "STOCK:update") || permission === undefined) {
        const isValidDate = (date) => date && !isNaN(new Date(date).getTime());

        setISEditStockData({
          ...cItem,
          mrp: CurrencyConverter.toCurrency(cItem?.mrp || 0, "INR"),
          saleprice: CurrencyConverter.toCurrency(cItem?.saleprice || 0, "INR"),
          discount: CurrencyConverter.toCurrency(cItem?.discount || 0, "INR"),
          baseprice: CurrencyConverter.toCurrency(cItem?.baseprice || 0, "INR"),
          taxamount: CurrencyConverter.toCurrency(cItem?.taxamount || 0, "INR"),
          landingcost: CurrencyConverter.toCurrency(
            cItem?.landingcost || 0,
            "INR",
          ),
          manufacturedate: isValidDate(cItem?.manufacturedate)
            ? DateConverter.ISOToDisplayFormat(
                DateConverter.toISO(
                  formatDate(new Date(cItem.manufacturedate)),
                  "yyyy-MM-dd",
                  isUser?.timezone,
                ),
              ).date
            : "",
          expiredate: isValidDate(cItem?.expiredate)
            ? DateConverter.ISOToDisplayFormat(
                DateConverter.toISO(
                  formatDate(new Date(cItem.expiredate)),
                  "yyyy-MM-dd",
                  isUser?.timezone,
                ),
              ).date
            : "",
          cIndex,
        });

        setStockEditModalOpen(true);
      } else {
        showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
      }
    },
    [
      hasPermission,
      permission,
      isUser?.timezone,
      setStockEditModalOpen,
      showAlert,
    ],
  );

  const handleClickBarcode = (code) => {
    if (
      hasPermission("PRODUCT", "PRODUCT:viewByCode") ||
      permission === undefined
    ) {
      setSelectedBarcode(code); // set the selected barcode
      setShowBarcodeModal(true); // show the modal
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleStockpdate = (product) => {
    // console.log("product", product);
    //   if(product.stock>0){
    //   if(product.stockIn<product.stock){
    //     console.log("trashin")
    //   }else if(product.stockIn>product.stock){
    //      if(product.stockLeft>product.stock){
    //       console.log("trashout")
    //      }else{
    //       console.log("insufficient Stock")
    //      }
    //   }else{
    //     console.log("euql")
    //   }
    // }
    handleUpdate(product);

    // setStockEditData((prv)=>[
    //   {...prv},
    //   {
    //     ...product
    //   }

    // ])
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "sno",
        header: "S No",
        size: 70,
        cell: ({ row }) => row.index + 1,
      }),

      columnHelper.accessor("createdAt", {
        header: "Date",
        size: 120,
        cell: ({ getValue }) =>
          DateConverter.ISOToDisplayFormat(
            DateConverter.convertToTimeZone(getValue(), isUser.timezone),
          ).date,
      }),

      columnHelper.accessor("createdAt", {
        id: "time",
        header: "Time",
        size: 110,
        cell: ({ getValue }) =>
          DateConverter.ISOToDisplayFormat(
            DateConverter.convertToTimeZone(getValue(), isUser.timezone),
          ).time,
      }),

      columnHelper.accessor("code", {
        header: "SKU",
        size: 150,
      }),

      columnHelper.accessor("title", {
        header: "Title",
        size: 280,
        cell: ({ row }) => (
          <div
            title={row.original.title}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {row.original.productid ? row.original.title : ""}
          </div>
        ),
      }),

      columnHelper.accessor("mrp", {
        header: "MRP",
        size: 90,
        cell: ({ getValue }) =>
          `₹${CurrencyConverter.toCurrency(getValue() || 0, "INR")}`,
      }),

      columnHelper.accessor("saleprice", {
        header: "Sale Price",
        size: 90,
        cell: ({ getValue }) =>
          `₹${CurrencyConverter.toCurrency(getValue() || 0, "INR")}`,
      }),

      columnHelper.accessor("taxpercentage", {
        header: "Tax %",
        size: 90,
        cell: ({ getValue }) => (
          <span className="badge bg-label-peace">
            {getValue() ? `${getValue()}%` : "0%"}
          </span>
        ),
      }),

      columnHelper.accessor("landingcost", {
        header: "Landing",
        size: 100,
        cell: ({ getValue }) =>
          `₹${CurrencyConverter.toCurrency(getValue() || 0, "INR")}`,
      }),

      columnHelper.accessor("expiredate", {
        header: "Expire",
        size: 110,
        cell: ({ row }) =>
          row.original.expiredate ? (
            <span className="badge bg-label-wood">
              {
                DateConverter.ISOToDisplayFormat(
                  DateConverter.convertToTimeZone(
                    row.original.expiredate,
                    isUser.timezone,
                  ),
                ).date
              }
            </span>
          ) : (
            ""
          ),
      }),

      columnHelper.display({
        id: "stockIn",
        header: "Stock In",
        size: 110,
        cell: ({ row }) => (
          <span className="badge bg-label-spring">
            {Number(
              row.original.isSecondaryUnit
                ? secondaryQuanity(
                    row.original.stockIn,
                    row.original.unitConfig,
                  )
                : row.original.stockIn,
            )}

            <span className="ms-1 font-11">
              {row.original.isSecondaryUnit
                ? row.original.unitConfig?.secondaryUnitCode
                : row.original.unitConfig?.primaryUnitCode}
            </span>
          </span>
        ),
      }),

      columnHelper.display({
        id: "stockLeft",
        header: "Stock Left",
        size: 110,
        cell: ({ row }) => (
          <span className="badge bg-label-space">
            {Number(
              row.original.isSecondaryUnit
                ? secondaryQuanity(
                    row.original.stockLeft,
                    row.original.unitConfig,
                  )
                : row.original.stockLeft,
            )}

            <span className="ms-1 font-11">
              {row.original.isSecondaryUnit
                ? row.original.unitConfig?.secondaryUnitCode
                : row.original.unitConfig?.primaryUnitCode}
            </span>
          </span>
        ),
      }),
    ],
    [isUser],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 46,
    overscan: 20,
  });

  return (
    <>
      <>
        <div className="mb-2">
          <table className="table table-bordered table-sm mb-0">
            <tbody>
              <tr>
                <td className="fw-600 text-center">STOCK REPORT</td>
              </tr>
            </tbody>
          </table>
        </div>

        {isPreLoading ? (
          <table className="table table-bordered table-sm">
            <tbody>
              {[...Array(15)].map((_, index) => (
                <tr key={index}>
                  {[...Array(columns.length)].map((__, i) => (
                    <td key={i}>
                      <Skeleton height={28} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div
            ref={tableContainerRef}
            className="border rounded"
            style={{
              height: "70vh",
              overflow: "auto",
              position: "relative",
            }}
          >
            <table
              className="table table-bordered table-hover table-sm mb-0"
              style={{
                width: "100%",
                tableLayout: "fixed",
              }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 8,
                  background: "#fff",
                }}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          width: header.getSize(),
                          minWidth: header.getSize(),
                          background: "#fff",
                          position: "sticky",
                          top: 0,
                          zIndex: 21,
                          textAlign: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  position: "relative",
                }}
              >
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="text-center p-4">
                      No Stock Found
                    </td>
                  </tr>
                )}

                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const row = rows[virtualRow.index];

                  return (
                    <tr
                      key={row.id}
                      onClick={() =>
                        openEditModal(row.original, virtualRow.index)
                      }
                      className={`cursor-pointer hover-row ${
                        row.original.expiredate
                          ? getColor(row.original.expiredate)
                          : ""
                      }`}
                      style={{
                        position: "absolute",
                        transform: `translateY(${virtualRow.start}px)`,
                        width: "100%",
                        display: "table",
                        tableLayout: "fixed",
                        height: "48px",
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          style={{
                            width: cell.column.getSize(),
                            minWidth: cell.column.getSize(),
                            textAlign: "center",
                            verticalAlign: "middle",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            height: "48px",
                            padding: "8px 12px",
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
      {isStockEditModalOpen && (
        <EditStock
          editData={isEditStockData}
          onClose={closeEditModal}
          onEdit={handleStockpdate}
          isBtnLoading={isLoading}
        />
      )}
      {productViewOpen && (
        <ViewStockDetails
          editData={isEditStockData}
          onClose={handlecloseView}
          closeEditModal={closeEditModal}
          handleStockpdate={handleStockpdate}
          isStockEditModalOpen={isStockEditModalOpen}
          setStockEditModalOpen={setStockEditModalOpen}
          setStockOutModalOpen={setStockOutModalOpen}
          isStockOutModalOpen={isStockOutModalOpen}
          closeStockOutModal={closeStockOutModal}
          handleStockTrash={handleStockTrash}
        />
      )}
      {showBarcodeModal && uRLs.BARCODE_PRINT === "true" && (
        <BarcodeGenerator
          barcodeValue={selectedBarcode.code}
          product={selectedBarcode}
          onClose={() => setShowBarcodeModal(false)}
        />
      )}
    </>
  );
};

export default ViewStockReport;
